import React, { useEffect, useState } from "react";
import { Grid, Box, CircularProgress, Typography, Alert } from "@mui/material";
import ContactsIcon from "@mui/icons-material/Contacts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
import KPICard from "./KPICard";
import BarChartWidget from "../Charts/BarChartWidget";
import PieChartWidget from "../Charts/PieChartWidget";
import {
  getMarketingLeadConversion,
  getMarketingCampaignPerformance,
  getMarketingClientSegmentation,
  getMarketingAgentPerformance,
  DateRangeParams,
} from "../../Services/reportsService";
import { Vocabulary } from "../../Utils/Vocabulary";

interface MarketingReportsProps {
  dateRange: DateRangeParams;
  onDataChange?: (data: any) => void;
}

const MarketingReports: React.FC<MarketingReportsProps> = ({ dateRange, onDataChange }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [leadConversion, setLeadConversion] = useState<any>(null);
  const [campaignPerformance, setCampaignPerformance] = useState<any>(null);
  const [clientSegmentation, setClientSegmentation] = useState<any>(null);
  const [agentPerformance, setAgentPerformance] = useState<any>([]);

  useEffect(() => {
    fetchMarketingData();
  }, [dateRange]);

  const fetchMarketingData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        leadConversionData,
        campaignPerformanceData,
        clientSegmentationData,
        agentPerformanceData,
      ] = await Promise.all([
        getMarketingLeadConversion(dateRange),
        getMarketingCampaignPerformance(dateRange),
        getMarketingClientSegmentation(),
        getMarketingAgentPerformance(dateRange),
      ]);

      setLeadConversion(leadConversionData);
      setCampaignPerformance(campaignPerformanceData);
      setClientSegmentation(clientSegmentationData);
      setAgentPerformance(agentPerformanceData);

      // Prepare data for export
      if (onDataChange) {
        const leadData = leadConversionData?.data || leadConversionData;
        const campaignData = campaignPerformanceData?.data || campaignPerformanceData;
        const segmentationData = clientSegmentationData?.data || clientSegmentationData;
        const agentData = agentPerformanceData?.data || agentPerformanceData;

        const exportData = {
          kpis: {
            totalLeads: leadData?.summary?.totalContacts || 0,
            finishedLeads: leadData?.summary?.finishedContacts || 0,
            conversionRate: leadData?.summary?.conversionRate || 0,
            leadTemplates: campaignData?.leadTemplatesCount || 0,
          },
          leadConversion: leadData?.byStatus?.map((item: any) => ({
            status: item.status,
            count: item.count,
            percentage: item.percentage || 0,
          })) || [],
          campaignPerformance: (campaignData?.methodPerformance || []).map((item: any) => ({
            campaign: item.method,
            leads: item.totalSent || 0,
            successful: item.successful || 0,
            successRate: item.successRate || 0,
          })),
          clientSegmentationByCategory: segmentationData?.byCategory?.map((item: any) => ({
            category: item.categoryName,
            count: item.count,
            percentage: item.percentage || 0,
          })) || [],
          clientSegmentationByPeriodicity: segmentationData?.byPeriodicity?.map((item: any) => ({
            periodicity: item.periodicityType,
            count: item.count,
            percentage: item.percentage || 0,
          })) || [],
          agentPerformance: (Array.isArray(agentData) ? agentData : []).map((item: any) => ({
            agent: item.agentName || item.agent,
            contacts: item.totalContacts || item.contacts,
            leads: item.finishedContacts || item.leads,
          })),
          agentConversionRate: (Array.isArray(agentData) ? agentData : []).map((item: any) => ({
            agent: item.agentName || item.agent,
            conversionRate: item.conversionRate || 0,
          })),
        };
        onDataChange(exportData);
      }
    } catch (err: any) {
      console.error("Error fetching marketing data:", err);
      setError(err.message || Vocabulary.errorGeneratingReport);
      if (onDataChange) {
        onDataChange(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{Vocabulary.loadingReport}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!leadConversion || !leadConversion.summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{Vocabulary.noDataAvailable}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.totalLeads}
            value={leadConversion.summary.totalContacts}
            subtitle={Vocabulary.totalContacts}
            icon={<ContactsIcon sx={{ fontSize: 40, color: "#1976d2" }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.finishedLeads}
            value={leadConversion.summary.finishedContacts}
            subtitle={Vocabulary.leadConversion}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: "#2e7d32" }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.conversionRate}
            value={`${leadConversion.summary.conversionRate}%`}
            subtitle={Vocabulary.successRate}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: "#ed6c02" }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.leadTemplates}
            value={campaignPerformance?.leadTemplatesCount || 0}
            subtitle={`${campaignPerformance?.activeTemplates || 0} active`}
            icon={<CampaignIcon sx={{ fontSize: 40, color: "#9c27b0" }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Lead Conversion by Status */}
        <Grid item xs={12} lg={6}>
          <BarChartWidget
            title={Vocabulary.leadConversion}
            data={leadConversion.contactsByStatus}
            xKey="status"
            yKey="count"
            yLabel={Vocabulary.count}
            color="#1976d2"
            height={350}
          />
        </Grid>

        {/* Campaign Performance by Method */}
        <Grid item xs={12} lg={6}>
          <BarChartWidget
            title={Vocabulary.campaignPerformance}
            data={campaignPerformance?.methodPerformance || []}
            xKey="method"
            yKey="successRate"
            yLabel={Vocabulary.successRate}
            color="#2e7d32"
            height={350}
          />
        </Grid>

        {/* Client Segmentation by Category */}
        <Grid item xs={12} lg={6}>
          <PieChartWidget
            title={Vocabulary.clientSegmentation}
            data={clientSegmentation?.byCategory || []}
            nameKey="categoryName"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Client Segmentation by Periodicity */}
        <Grid item xs={12} lg={6}>
          <PieChartWidget
            title="Clienți după Periodicitate"
            data={clientSegmentation?.byPeriodicity || []}
            nameKey="periodicityType"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Agent Performance */}
        <Grid item xs={12}>
          <BarChartWidget
            title={Vocabulary.agentPerformance}
            data={agentPerformance}
            xKey="agentName"
            yKey="totalContacts"
            yLabel={Vocabulary.contactsMade}
            color="#9c27b0"
            height={350}
          />
        </Grid>

        {/* Agent Conversion Rate */}
        <Grid item xs={12}>
          <BarChartWidget
            title="Rată de Conversie Agenți"
            data={agentPerformance}
            xKey="agentName"
            yKey="conversionRate"
            yLabel={Vocabulary.conversionRate}
            color="#ed6c02"
            height={350}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingReports;
